import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, SimpleChange, HostListener } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Observable, concat, from, of, EMPTY, finalize, Subscription, Subject } from 'rxjs';
import { map, switchMap, concatMap, debounceTime, distinctUntilChanged, filter, catchError } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';
import { round1, round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';
import { Product, ProductInfo } from 'src/app/shared/product';
import { ProductApiService, StockResult } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { IncomePriceBreakdownDialogComponent } from 'src/app/visualcomponents/income-price-breakdown-dialog/income-price-breakdown-dialog.component';

/**
 * Inline-editing table for stock inventory documents.
 *
 * **Pipeline architecture:**
 * - `docItemList$` — debounced `switchMap(getDocItemsByDoc)` that reloads rows when the
 *   document ID changes; triggers `calculateDocItemsFields` enrichment per row.
 * - `calcDocSum$` — debounced (100 ms) Subject with a simple `map` aggregation (not
 *   `concatMap` like other variants); does NOT emit `getDocumentStatus`.
 *
 * **Depot IDs:** `ms` (main stock), `is` (inventory stock), and `as` (auxiliary stock) are
 * resolved from global settings and passed to stock-level API calls.
 *
 * **Row enrichment (`calculateDocItemsFields`):**
 * `getProductLcStockBefore` → `DOCITEMLOCALSTOCK` (local stock at doc date),
 * `getProductLcStockAfter` → `U4ETKA` (stock after date), `getProdInfoById`.
 *
 * **Product selection (`onProductSelection`):** full synchronous enrichment from
 * `getProdInfoById + getProductLcStockBefore + getProdIncomePriceById`; uses local stock
 * income price when available.
 *
 * **Space key handler:** `@HostListener('keydown.space')` is IMPLEMENTED (unlike other
 * variant components) — appends a space in the focused input.
 *
 * **Single-select:** `sDI` is a single `DocItems` object (not an array).
 *
 * Column visibility is persisted in `localStorage` under key `edit-document-table-columns-14`.
 */
@Component({
  selector: 'app-edit-document-content-inventory',
  templateUrl: './edit-document-content-inventory.component.html',
  styleUrls: ['./edit-document-content-inventory.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentInventoryComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData = null;

  private docItemQuery: Subscription = null;
  private docItemList$ = new Subject<number>();

  private diSumQuery: Subscription = null;
  private diSaveQuery: Subscription = null;
  private diGroupQuery: Subscription = null;

  private _prodShippedQuery: Subscription = null;

  /** Debounced Subject that re-aggregates document totals. */
  private calcDocSum$ = new Subject<Document>();
  private calcDocSumSubs: Subscription = null;

  /** Main stock depot ID resolved from global settings. */
  private ms: number = 0;
  /** Inventory depot ID resolved from global settings. */
  private is: number = 0;
  /** Auxiliary stock depot ID resolved from global settings. */
  private as: number = 0;

  /** Whether to render the table header toolbar. */
  @Input() showHeader: boolean = true;
  /** Whether to show add/edit/delete action controls. */
  @Input() allowEditControls: boolean = true;

  loading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number = null;

  sDI: DocItems = null;

  allDocItemsChanged: boolean = false;

  docItems: DocItems[] = [];

  docItemsCopy: DocItems[] = [];
  docItemsClone: DocItems[] = [];

  @ViewChild('docItemTable') docItemTable: Table;
  @ViewChild('priceBreakdownDialog') priceBreakdownDialog: IncomePriceBreakdownDialogComponent;

  @HostListener('keydown.space', ['$event'])
  /** Prevents PrimeNG table from hijacking the spacebar and appends a space to the focused input value. */
  onSpaceKeyDown(event: KeyboardEvent) {
    event.stopPropagation();
    event.preventDefault();
    if (event.target) {
      event.target['value'] = event.target['value'] + ' ';
      (event.target as HTMLElement).focus();
    }
  }

  editMode: boolean = false;
  confirmDeleteOn: boolean = false;

  /** The document whose line items are being edited. */
  @Input() inputDocument: Document = null;

  /** Reserved trigger input — currently unused, kept for API compatibility. */
  @Input() knockKnock: boolean = false;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-14';

  showDocRowsOptions = [
    { name: 'Без НДС', value: 2 },
    { name: 'УКТЗЕД', value: 4 },
    { name: 'Резерв', value: 8 },
    { name: 'Примечание', value: 16 },
    { name: 'Входная', value: 32 },
  ];

  showFilterOptions = [
    { name: 'Инвойс', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  allowOld: boolean = false;

  /** Overrides column-visibility bitmask from the caller; skips localStorage restore when set. */
  @Input() setShowRowOptions: number[] = [];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  /*
   *  TODO: load access right to check is U4ETKA is visible to
   */
  public get seeWoVAT(): boolean { return this.showRowOptions.includes(2); }

  public get seeTariffCode(): boolean { return this.showRowOptions.includes(4); }

  public get seeReservation(): boolean { return this.showRowOptions.includes(8); }

  public get seeDiNote(): boolean { return this.showRowOptions.includes(16); }

  /**
   * True only when a row is selected AND still on screen. A selected row that the
   * "empty rows" switch has since hidden must not stay targetable by delete, or the
   * user would act on a row they cannot see. (`sDI` is kept, so the row is selected
   * again if it becomes visible.)
   */
  public get selection(): boolean { return !!this.sDI && this.isDocItemRowVisible(this.sDI, false); }

  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public get seeProfit(): boolean { return this.showRowOptions.includes(32); }

  public get allowTopButtons(): boolean { return this.docItems && (this.docItems.length > 10); }

  public allowSave(di: DocItems): boolean { return di && (di.PRODID ? true : false); }

  cf3: any = {};

  /** Emits `true` when entering edit mode, `false` when edit is saved or cancelled. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();

  /** When true, rows with zero `DOCITEMQTY` are shown in the table and included in printed forms; when false they are hidden. */
  @Input() countEmpty: boolean = false;

  /** Two-way binding companion for `countEmpty`; emits when the user toggles zero-qty inclusion. */
  @Output() countEmptyChange: EventEmitter<boolean> = new EventEmitter();
  constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) {

    this.calcDocSumSubs = this.calcDocSum$.pipe(

      debounceTime(100),
      filter(doc => (doc !== null)),

      map(doc => {


        // Same zero-quantity inclusion rule as the table body and the XLSX export.
        doc.DOCITEMS = this.docItems.filter(e => this.isDocItemRowVisible(e, false)).length;

        doc.DOCTOTALITEMS = this.docItems.reduce((r, di) => r + di.DOCITEMQTY, 0);
        doc.DOCSUMU4ETKA = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.U4ETKA, 0);
        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);
        doc.DOCSUMPROFIT = this.docItems.reduce((r, di) => r + di.DOCITEMPROFITSUM, 0);
      })
    )
      .subscribe();

    this.docItemQuery =
      this.docItemList$.pipe(
        debounceTime(300),
        switchMap(docId => {
          this.loading = true;
          console.log('*** EditDocumentContentInventoryComponent docItemList$ subscribing....: ' + docId)

          return this.docApi.getDocItemsByDoc(docId).pipe(
            map(
              data => this.calculateDocItemsFields(data)     // <= Raw docItems[] from database
            ),
            finalize(() => {
              this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
              // <= Here we have also document summary calculated
              this.loading = false;
            })
          );
        })
      )
        .subscribe();
  }

  reloadDocContent(event) {
    console.log('*** EditDocumentContentInventoryComponent reloadDocContent: ' + JSON.stringify(event))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }
  }

  saveDiCopy() {
    this.docItemsCopy = [];
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));
  }

  restoreDiCopy() {
    this.docItems = [];
    this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));
  }

  /** Computes retail price, row sums, and warning flags (`cnm`, `rnm`, `mtc`, `hlt`) for one row. */
  calculateRowSummary(di: DocItems): DocItems {
    //console.log('*** EditDocumentContentInventoryComponent calculateRowSummary: ' + di.DOCITEMID);

    di.DOCITEMRETPRICE = di.prodInfo.PRODPRICE * this.inputDocument.DOCRATEVAL;
    di.DOCITEMRETVPRICE = di.prodInfo.PRODPRICE;

    di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
    di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);
    di.mtc = ((di.PRODCUSTCODE || '') == '');

    di.hlt = (di.cnm || di.rnm || di.mtc);

    di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
    di.DOCITEMSUM = di.DOCITEMQTY * di.DOCITEMPRICE;
    di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;

    this.calculateDocumentSummary();

    return di;
  }

  calculateDocumentSummary() {
    this.calcDocSum$.next(this.inputDocument);
  }

  /**
   * Enriches raw `DocItems[]` with local stock before/after the document date and product info
   * via sequential `concatMap` calls against `getProductLcStockBefore`, `getProductLcStockAfter`,
   * and `getProdInfoById`.
   */
  calculateDocItemsFields(data: DocItems[]) {
    //console.log('*** EditDocumentContentInventoryComponent calculateDocItemsFields')
    this.docItems = [...data];

    this.diSumQuery = from(this.docItems).pipe(

      concatMap(di => {
        di.cloneIndex = 0;
        di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
        di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
        di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));
        di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);
        di.DOCITEMLOCALSTOCK = 0;

        return of(di);
      }),

      concatMap(di => this.prodApi.getProductLcStockBefore(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCMANDEPID, this.inputDocument.DOCLCOCLIID, this.inputDocument.DOCDATE).pipe(
        map(stock => {
          di.DOCITEMLOCALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
          return di;
        }),
        catchError(this.handleHttpError('getProductLcStockBefore', di))
      )),

      concatMap(di => this.prodApi.getProductLcStockAfter(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCMANDEPID, this.inputDocument.DOCLCOCLIID, this.inputDocument.DOCDATE).pipe(
        map(stock => {
          di.U4ETKA = (stock as StockResult[])[0]?.stock ?? 0;
          return di;
        }),
        catchError(this.handleHttpError('getProductLcStockAfter', di))
      )),

      concatMap(di => this.prodApi.getProdInfoById(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCCURRID, this.ms, this.is, this.as, new Date()).pipe(
        map(stockInfo => {
          di.prodInfo = stockInfo[0];

          // Below calculation is for the case when nothing really is on the stock

          di.DOCITEMGLOBALSTOCK = di.prodInfo.PRODSTOCK;
          di.DOCINCOMEPRICE = di.prodInfo.PRODINPRICE * this.inputDocument.DOCRATEVAL;

          return di;
        }),
        catchError(this.handleHttpError('getProdInfoById', di))
      )),

      concatMap(di => of(this.calculateRowSummary(di))),

      finalize(() => {
        environment.production || console.log('*** EditDocumentContentInventoryComponent calculateDocItemsFields => finalize(docItems)');
        environment.production || console.table(this.docItems);

        this.inputDocument.items = this.docItems;

        this.saveDiCopy();
      })
    ).subscribe();
  }

  private handleHttpError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      const detail = error?.status === 0 ? `Немає з'єднання з сервером`
        : error?.status >= 500 ? 'Помилка сервера'
        : error?.message || 'Невідома помилка';
      this.messageService.add({ severity: 'error', summary: 'Помилка завантаження', detail, life: 5000 });
      return result !== undefined ? of(result) : (EMPTY as Observable<T>);
    };
  }

  /** Persists column visibility to localStorage and unsubscribes all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentContentInventoryComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }
    if (this._prodShippedQuery) { this._prodShippedQuery.unsubscribe(); }
    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }
    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }
    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }
    if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
  }

  /**
   * Reacts to `inputDocument`, `knockKnock`, and `setShowRowOptions` changes.
   * - `inputDocument`: pushes the document ID onto `docItemList$` to reload rows.
   * - `knockKnock`: force-reloads rows for the current document.
   * - `setShowRowOptions`: overrides the column visibility bitmask immediately.
   */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentInventoryComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentInventoryComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentInventoryComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        case 'knockKnock':
          this.docItemList$.next(this.inputDocument.DOCID);
          break;

        case 'inputDocument':
          if (change.currentValue) {
            this.docItemList$.next(change.currentValue.DOCID);
          }
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, resolves depot IDs from global settings, restores column state from localStorage, and wires up `docItemList$` and `calcDocSum$` pipelines. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentInventoryComponent ngOnInit: ')

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.ms = this.globalSettinsData.mainStockId;
    this.is = this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0;
    this.as = this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0;

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }

    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
  }

  /**
   * Handles product selection from the inline product picker.
   * Fetches `getProdInfoById`, `getProductLcStockBefore`, and `getProdIncomePriceById`
   * in sequence; uses local stock income price when local stock > 0.
   */
  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentInventoryComponent onProductSelection: ' + JSON.stringify(event));

    var doc = this.inputDocument;

    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;
    this.loading = true;

    this._prodShippedQuery =
      this.prodApi.getProdInfoById(di.PRODID, doc.DOCMANCLIID, doc.DOCCURRID, doc.DOCMANDEPID, 0, 0, new Date())
      .pipe(
        concatMap(data => {
          di.prodInfo = data[0];

          di.DOCITEMPRODNAME = event.PRODNAME;
          di.DOCITEMGLOBALSTOCK = di.prodInfo.PRODSTOCK;

          return of(di);
        }),

        concatMap(di => this.prodApi.getProductLcStockBefore(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCLCOCLIID, doc.DOCDATE).pipe(
          map(stock => {
            di.DOCITEMLOCALSTOCK = (stock as StockResult[])[0]?.stock ?? 0;
            return di;
          }),
          catchError(this.handleHttpError('getProductLcStockBefore-selection', di))
        )),

        concatMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE).pipe(
          map(price => {
            di.DOCINCOMEPRICE =
            di.DOCITEMPRICE = round2(((di.DOCITEMLOCALSTOCK > 0) ? price[0].PRODINCOMEPRICE : di.prodInfo.PRODINPRICE) * doc.DOCRATEVAL);

            di.DOCITEMPRICEWOVAT = (di.DOCITEMPRICE) / ((100.0 + di.PRODVAT) / 100.0);
            di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / doc.DOCRATEVAL);

            return this.calculateRowSummary(di);
          }),
          catchError(this.handleHttpError('getProdIncomePriceById-selection', di))
        )),

        catchError(this.handleHttpError('onProductSelection')),
        finalize(() => {
          this.loading = false;
        })
      )
        .subscribe();
  }

  /**
   * Opens the FIFO income-price breakdown popup in single-depot mode — `DOCINCOMEPRICE` here
   * comes from a single `getProdIncomePriceById(..., doc.DOCMANDEPID, ...)` call (see
   * `onProductSelection` above), the same single-depot shape as
   * `edit-document-content-receipt`'s price, not `edit-document-content`'s 3-depot blend.
   *
   * Note `U4ETKA` in this component holds post-date stock quantity ("После"), not a
   * value-currency price like in the receipt component — do not reuse it as the cell's
   * value-currency comparison. There is no stored value-currency income-price field here,
   * so it's derived the same way `DOCITEMVPRICE` already is from `DOCITEMPRICE`
   * (`calculateDocItemsFields` above): `DOCINCOMEPRICE / DOCRATEVAL`.
   */
  openIncomePriceBreakdown(docItem: DocItems): void {
    const doc = this.inputDocument;
    const cellPriceMain = docItem.DOCINCOMEPRICE ?? null;
    const cellPriceCurrency = (cellPriceMain !== null && doc.DOCRATEVAL) ? round2(cellPriceMain / doc.DOCRATEVAL) : null;

    this.priceBreakdownDialog.open(
      docItem.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID,
      docItem.DOCITEMQTY, doc.DOCDATE,
      cellPriceCurrency, doc.DOCRATEVAL ?? null, cellPriceMain,
    );
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentInventoryComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    let newDi: DocItems = {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0,
      DOCITEMLOCALSTOCK: 0,

    };

    this.docItems = [...this.docItems, newDi];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentInventoryComponent in onPpRowEditInit: entry');
    //environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentInventoryComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.editing.emit(this.editMode);
  }

  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentInventoryComponent in onPpRowEditCancel: ' + index);
    //environment.production || console.table(esg);

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.DOCITEMID == -2) {
      // Cancel the newly created record
      // simply delete

      this.docItems.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.docItems[index] = { ...this.docItemsClone[ppid] };
      this.docItems[index].cloneIndex = 0;
    }

    this.docItemsClone.splice(ppid, 1);

    this.editMode = false;
    this.editing.emit(this.editMode);
  }

  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentInventoryComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    esg.DOCITEMVPRICE = esg.DOCITEMPRICE / esg.DOCITEMCRATE;

    this.diSaveQuery =
      concat(((esg.DOCITEMID == -2) ? this.docApi.postDocItemById(esg) : this.docApi.putDocItemById(esg))
        .pipe(map(count => {
          console.log('*** EditDocumentContentInventoryComponent in onSubscribe to Save: ' + count);

          this.docItemsClone.splice(ppid, 1);
          esg.cloneIndex = 0;
          this.reloadDocContent(null);
          this.editMode = false;
          this.editing.emit(this.editMode);
        })))
        .subscribe();
  }

  onDOCITEMQTYChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMQTYChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    this.calculateRowSummary(di);
  }

  onDOCITEMVPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMVPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round2((di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEWOVATChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMPRICEWOVATChange: ' + JSON.stringify(di));

    di.DOCITEMPRICE = round2(round1(di.DOCITEMPRICEWOVAT) * ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);
    di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentInventoryComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onPAYITEMCLIIDChange(event, di: DocItems) {
  }

  checkDiState(event, di: DocItems) {
    console.log('*** EditDocumentContentInventoryComponent in checkDiState: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentInventoryComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {
      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
          + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
          + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
          + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
      });
    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }

  }

  onRowSelect(event) {
    console.log('*** EditDocumentContentInventoryComponent in onRowSelect: ' + JSON.stringify(event));
  }

  updCurrPrice(event) {
    console.log('*** EditDocumentContentInventoryComponent in updCurrPrice: ' + JSON.stringify(event));
    this.diGroupQuery
      = from(this.docItems)
        .pipe(switchMap(di => of(di)))
        .subscribe(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
          this.calculateRowSummary(e);
        });

    this.allDocItemsChanged = true;
    this.editing.emit(this.allDocItemsChanged);
  }

  updMainPrice(event) {
    console.log('*** EditDocumentContentInventoryComponent in updMainPrice: ' + JSON.stringify(event));
    this.diGroupQuery
      = from(this.docItems)
        .pipe(switchMap(di => of(di)))
        .subscribe(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMPRICE = round2(e.DOCITEMVPRICE * e.DOCITEMCRATE);
          this.calculateRowSummary(e);
        });

    this.allDocItemsChanged = true;
    this.editing.emit(this.allDocItemsChanged);
  }

  onSaveRows(event) {
    console.log('*** EditDocumentContentInventoryComponent in onSaveRows: ' + JSON.stringify(event));

    let count = this.docItems.length;

    this.docItems.forEach(e => {
      this.diSaveQuery = this.docApi.putDocItemById(e).pipe(map(() => { this.reloadDocContent(null); })).subscribe(() => {
        console.log('*** EditDocumentContentInventoryComponent in onSaveRows: count: ' + count);
        if (--count == 0) {
          this.calculateDocItemsFields(this.docItems);
          this.allDocItemsChanged = false;
          this.editing.emit(this.allDocItemsChanged);
        }
      });
    });
  }

  onUndoRows(event) {
    console.log('*** EditDocumentContentInventoryComponent in onUndoRows: ' + JSON.stringify(event));

    this.restoreDiCopy();
    this.calculateDocItemsFields(this.docItems);
    this.allDocItemsChanged = false;
    this.editing.emit(this.allDocItemsChanged);
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentInventoryComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentInventoryComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentInventoryComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentInventoryComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentInventoryComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentInventoryComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = null;
            this.reloadDocContent(null);
          });
      } else {
        if (this.sDI) {
          this.docApi.deleteDocItemById(this.sDI.DOCITEMID)
            .subscribe(() => {
              console.log('*** EditDocumentContentInventoryComponent onDeleteConfirmation: DONE!');
              this.sDI = null;
              this.reloadDocContent(null);
            });
        }
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentInventoryComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.isDocItemRowVisible(e, false))
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          'Цена без НДС': e.DOCITEMPRICEWOVAT,
          'Сумма без НДС': e.DOCITEMSUMWOVAT,
          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Текст 1': e.DOCITEMTEXT1,
          'Текст 2': e.DOCITEMTEXT2,

          'На складе': e.prodInfo.PRODSTOCK,
          'В резерве': e.prodInfo.PRODRESERVE,
          'Продано': e.prodInfo.PRODSOLD,
          'В пути': e.prodInfo.PRODONWAY,

          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,

          'Розница': e.DOCITEMRETPRICE,
          'Розница в валюте': e.DOCITEMRETVPRICE,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentInventoryComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentInventoryComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(state) {
    console.log('*** EditDocumentContentInventoryComponent cleanSortMode: ' + JSON.stringify(state));

    this.docItemTable.reset();
    this.reloadDocContent(state);
  }

  updateRowIndexes(state) {
    console.log('*** EditDocumentContentInventoryComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;

    this.diSaveQuery =
      concat(
        from(this.docItems)
          .pipe(concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          })),

        of(false).pipe(map(data => this.loading = data))
      )
        .subscribe();
  }

  countEmptyModeChange(event) {
    console.log('*** EditDocumentContentComponent countEmptyModeChange: ' + JSON.stringify(event));

    this.countEmpty = event.checked;

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
      this.countEmptyChange.emit(this.countEmpty);
    }
  }

  /**
   * Whether a row is rendered in the items table.
   *
   * When {@link countEmpty} is off, zero-quantity rows are hidden. A row currently being
   * edited stays visible even if its quantity is (or becomes) zero, so it does not vanish
   * under the user. The same predicate decides the row count (`DOCITEMS`) and the XLSX
   * export rows; {@link docItems} itself still holds every row for editing.
   *
   * @param di - The document item row.
   * @param editing - Whether PrimeNG reports the row as being edited.
   */
  isDocItemRowVisible(di: DocItems, editing: boolean): boolean {
    return this.countEmpty || editing || (di.DOCITEMQTY != null && di.DOCITEMQTY !== 0);
  }
}

